import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { ChangeRequestProgressInfo, BuyerChangeRequestStatus } from '@/types';
import { DateTimeDisplay } from '@/components/localization';
import BuyerChangeRequestOffer from './BuyerChangeRequestOffer';
import BuyerChangeRequestView from './BuyerChangeRequestView';
import { useIsMobile } from '@/utils/hooks';
import { orderBy } from 'lodash-es';
import { SimpleCard } from '@/components/Cards';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import theme from './BuyerChangeRequestDetails.module.scss';
import { BuyerChat } from '../BuyerChat';
import { Card, Tabs } from '@ygtechnologies/rubus-ui';
import { Breadcrumbs } from '@ygtechnologies/bygr-frontend-component-library';

interface Props {
    changeRequest: ChangeRequestProgressInfo;
    loadData: () => void;
}

const BuyerChangeRequestDetails: React.FC<Props> = ({ changeRequest, loadData }) => {
    const isMobile = useIsMobile();
    const [isViewOpen, setIsViewOpen] = useState(false);
    const { tabId } = useParams<{ tabId: string }>();
    const activeTabId = tabId ? tabId : 'chat';

    return (
        <>
            <div className={theme.container}>
                {isMobile && (
                    <Breadcrumbs>
                        <Breadcrumbs.Link to="/change-requests">
                            <Trans>Change requests</Trans>
                        </Breadcrumbs.Link>

                        <Breadcrumbs.Item>{changeRequest.subject}</Breadcrumbs.Item>
                    </Breadcrumbs>
                )}
                {/* 
                {isMobile && (
                    <CustomLink to="/change-requests" className={theme.mobileBackButton}>
                        <FontAwesomeIcon icon={faChevronLeft} /> <Trans>Change requests</Trans>
                    </CustomLink>
                )} */}
                {!isMobile && (
                    <Card className={theme.subject}>
                        <div className={theme.subjectRow}>
                            {changeRequest.subject}
                            <span className={theme.show} onClick={() => setIsViewOpen(true)}>
                                <Trans>Show request</Trans>
                            </span>
                        </div>
                    </Card>
                )}
                {isMobile && (
                    <Card>
                        <div className={theme.mobileSubject} onClick={() => setIsViewOpen(true)}>
                            {changeRequest.subject}
                        </div>
                        <div className={theme.date}>
                            <Trans>Sent on</Trans> <DateTimeDisplay value={changeRequest.submitDate} />
                        </div>
                    </Card>
                )}

                {changeRequest.status === BuyerChangeRequestStatus.Closed && (
                    <Card className={theme.closeCard}>
                        <div>
                            <h4>
                                <Trans>This change request has been closed</Trans>
                            </h4>
                            <div className={theme.closeNote}>{changeRequest.closeNote}</div>
                        </div>
                    </Card>
                )}

                <Tabs
                    className={theme.tabsHandle}
                    collapseToDropdownOnMobile={false}
                    activeId={activeTabId}
                    variant="underline">
                    <Tabs.Tab
                        id="chat"
                        as={Link}
                        to={`/change-requests/${changeRequest.changeRequestId}/chat`}
                        label={<Trans>Messages</Trans>}
                    />
                    <Tabs.Tab
                        id="offer"
                        as={Link}
                        to={`/change-requests/${changeRequest.changeRequestId}/offer`}
                        badge={changeRequest.status === BuyerChangeRequestStatus.OfferReceived ? 1 : 0}
                        label={<Trans>Offer</Trans>}
                    />
                </Tabs>
                {activeTabId === 'chat' && (
                    <BuyerChat
                        wrapperClassName={theme.tabContainer}
                        threadId={changeRequest.commentThreadId}
                        hideTopBar
                    />
                )}
                {activeTabId === 'offer' && (
                    <>
                        {changeRequest.offers.length === 0 && (
                            <div className={theme.noOffers}>
                                <Trans>You haven't received any offers yet.</Trans>
                            </div>
                        )}
                        {orderBy(changeRequest.offers, 'sendDate', 'desc').map((o) => (
                            <BuyerChangeRequestOffer
                                onChange={loadData}
                                key={o.id}
                                offer={o}
                                changeRequestId={changeRequest.changeRequestId}
                            />
                        ))}
                    </>
                )}
            </div>
            <BuyerChangeRequestView
                changeRequest={changeRequest}
                isOpen={isViewOpen}
                handleCloseClick={() => setIsViewOpen(false)}
            />
        </>
    );
};

export default BuyerChangeRequestDetails;
